// Fetch-based HTTP client with axios-compatible API.
// Preserves: {data, status, headers} response shape, interceptors-like auth+refresh,
// { params } for query strings, JSON auto-stringify, FormData passthrough,
// responseType: 'blob'/'text'/'arraybuffer'.
import type { BookingSignals, LegacyStayCalendarItem, LegacyStaysCalendarResponse } from '@guilleumes/types';
import { isPublicRoute } from '@/lib/constants/public-routes';
import { currentPath, saveReturnTo } from '@/lib/auth-return-to';

// Dynamic API URL: when accessing via LAN IP (e.g. mobile), replace 'localhost'
// with the current hostname so API calls reach the same machine
export function resolveApiUrl(): string {
  const configured = import.meta.env.VITE_API_URL || 'http://localhost:8001/api';
  if (
    typeof window !== 'undefined' &&
    window.location.hostname !== 'localhost' &&
    configured.includes('localhost')
  ) {
    return configured.replace('localhost', window.location.hostname);
  }
  return configured;
}

const API_BASE_URL = resolveApiUrl();

export interface RequestConfig {
  params?: Record<string, any>;
  headers?: Record<string, string>;
  responseType?: 'json' | 'blob' | 'text' | 'arraybuffer';
  signal?: AbortSignal;
  _retry?: boolean;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: RequestConfig & { url: string; method: string };
}

export class ApiError<T = any> extends Error {
  response?: ApiResponse<T>;
  config?: RequestConfig & { url: string; method: string };
  request?: Request;
  isApiError = true;
  constructor(message: string, response?: ApiResponse<T>) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
  }
}

function buildUrl(path: string, params?: Record<string, any>): string {
  const base = path.startsWith('http')
    ? path
    : `${API_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
  if (!params) return base;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, String(v)));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  if (!qs) return base;
  return base + (base.includes('?') ? '&' : '?') + qs;
}

function headersToObject(h: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  h.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

// ── Sessió: token d'accés, refresc i final de sessió ─────────────────────────────────
//
// Tot el que llegeix el token d'accés passa per aquí. Cap component ni hook ha de llegir
// `localStorage` pel seu compte: qui ho fa es queda fora del refresc i, amb un token d'accés
// curt (F4 de `plans/sessions-i-refresh-tokens.md`), rep un 401 a mitja feina.

/** El token d'accés desat, o `null`. Per ensenyar-lo (`/admin/tokens`) o saber si n'hi ha. */
export function getAccessToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('accessToken');
}

function getRefreshToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('refreshToken');
}

/**
 * El refresc no s'ha pogut fer, però **no** perquè el servidor hagi rebutjat la sessió: xarxa
 * caiguda, 5xx (un deploy), 429 (límit de ritme) o 400. La sessió es conserva i la petició
 * original falla amb aquest error, que no porta `response`: qui el rep el tracta com una
 * fallada de xarxa i es pot tornar a provar.
 */
export class SessionRefreshUnavailableError extends ApiError {
  constructor() {
    super("No s'ha pogut renovar la sessió. Torna-ho a provar d'aquí a un moment.");
    this.name = 'SessionRefreshUnavailableError';
  }
}

/**
 * - `refreshed`: hi ha token d'accés nou, ja desat.
 * - `rejected`: el servidor ha dit que la sessió no val (401 o 403). Ja s'ha esborrat i,
 *   fora de les rutes públiques, el navegador ja va cap a `/login`.
 * - `unavailable`: no s'ha pogut saber. No s'ha esborrat res.
 */
export type RefreshResult =
  | { outcome: 'refreshed'; accessToken: string }
  | { outcome: 'rejected' }
  | { outcome: 'unavailable' };

/**
 * Esborra la sessió i, des d'una ruta de l'app, porta a `/login` desant on s'era.
 * Les rutes públiques (`lib/constants/public-routes.ts`) no es redirigeixen mai: un hoste
 * que obre `/formulari/:token` en un mòbil amb un token vell no ha d'acabar al login.
 */
function endSession(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (typeof window !== 'undefined' && !isPublicRoute(window.location.pathname)) {
    saveReturnTo(currentPath());
    window.location.href = '/login';
  }
}

async function runRefresh(): Promise<RefreshResult> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return { outcome: 'rejected' };

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return { outcome: 'unavailable' };
  }

  // Si mentrestant algú ha tancat la sessió o ha tornat a entrar, aquesta resposta parla
  // d'una sessió que ja no és la d'ara: ni es desa el token ni s'esborra res.
  const stillSameSession = getRefreshToken() === refreshToken;

  if (res.status === 401 || res.status === 403) {
    if (stillSameSession) endSession();
    return { outcome: 'rejected' };
  }
  // 400 (token mal format per al DTO), 429 (límit de ritme), 5xx (deploy, SQLite ocupat) i
  // qualsevol altra cosa: no és un «no» del servidor a la sessió.
  if (!res.ok || !stillSameSession) return { outcome: 'unavailable' };

  try {
    const body = await res.json();
    const accessToken = body?.accessToken;
    if (typeof accessToken === 'string' && accessToken) {
      localStorage.setItem('accessToken', accessToken);
      return { outcome: 'refreshed', accessToken };
    }
  } catch {
    // Un 200 que no és JSON (una pàgina d'un proxy): tampoc és un «no».
  }
  return { outcome: 'unavailable' };
}

let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Demana un token d'accés nou amb el refresh token desat. Totes les crides que coincideixen
 * en el temps comparteixen UNA sola petició a `/auth/refresh`.
 */
export function refreshSession(options: { force?: boolean } = {}): Promise<RefreshResult> {
  if (refreshInFlight) return refreshInFlight;
  if (!options.force && isRefreshPaused()) {
    return Promise.resolve({ outcome: 'unavailable' });
  }
  const sentRefreshToken = getRefreshToken();
  // `.finally` corre en una microtasca posterior: mai abans de l'assignació.
  refreshInFlight = runRefresh()
    .then((result) => {
      // Només es recorda un «no disponible». Un `refreshed` o un `rejected` no frenen res.
      refreshPause =
        result.outcome === 'unavailable'
          ? { refreshToken: sentRefreshToken, until: Date.now() + REFRESH_UNAVAILABLE_PAUSE_MS }
          : null;
      return result;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * Després d'un refresc «no disponible», el temps que no se'n torna a demanar cap. Sense aquest
 * fre, cada 401 posterior en llança un de nou: una càrrega del calendari amb els sondejos en fa
 * desenes en segons i, amb el límit del backend (30/min per IP), deixa un minut sense refresc
 * tots els dispositius de l'hotel. Dins la pausa, les peticions fallen amb el mateix
 * `SessionRefreshUnavailableError` sense tocar la xarxa.
 */
export const REFRESH_UNAVAILABLE_PAUSE_MS = 3000;

/** La pausa va lligada al refresh token que ha fallat: un login nou no l'hereta. */
let refreshPause: { refreshToken: string | null; until: number } | null = null;

function isRefreshPaused(): boolean {
  if (!refreshPause) return false;
  if (Date.now() >= refreshPause.until || refreshPause.refreshToken !== getRefreshToken()) {
    refreshPause = null;
    return false;
  }
  return true;
}

/**
 * Aixeca la pausa: per a un reintent demanat per la persona («Torna-ho a provar») o perquè
 * ha tornat la xarxa (`online`). El proper 401 torna a provar el refresc de seguida.
 */
export function clearRefreshPause(): void {
  refreshPause = null;
}

type FetchInit = Omit<RequestInit, 'headers'> & { headers?: Record<string, string> };

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:/i;

function originOf(url: string): string | null {
  const base = (typeof window !== 'undefined' && window.location?.origin) || undefined;
  try {
    return new URL(url, base).origin;
  } catch {
    return null;
  }
}

/**
 * La URL és de la nostra API? El token d'accés només viatja cap a l'origen d'`API_BASE_URL`:
 * una URL absoluta d'un altre lloc surt sense `Authorization`, i un 401 seu no és cosa de la
 * nostra sessió.
 */
function isApiUrl(url: string): boolean {
  if (!ABSOLUTE_URL.test(url) && !ABSOLUTE_URL.test(API_BASE_URL)) return !url.startsWith('//');
  const origin = originOf(url);
  return origin !== null && origin === originOf(API_BASE_URL);
}

function hasAuthorization(headers: Record<string, string> | undefined): boolean {
  return !!headers && Object.keys(headers).some((k) => k.toLowerCase() === 'authorization');
}

function withToken(init: FetchInit, token: string | null): RequestInit {
  const headers: Record<string, string> = { ...(init.headers || {}) };
  if (token && !hasAuthorization(headers)) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return { ...init, headers };
}

/** Un cos que és un stream només es pot enviar un cop: no es pot repetir la petició. */
function isReplayableBody(body: unknown): boolean {
  return !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream);
}

/** Un 401 del login o del mateix refresc no parla del token d'accés: no es refresca. */
function isSessionEndpoint(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return path.endsWith('/auth/login') || path.endsWith('/auth/refresh');
}

/**
 * `fetch` amb el token d'accés i el circuit 401 → refresc → UN reintent. Torna la `Response`
 * tal com arriba, sense llegir-ne el cos.
 *
 * El reintent només es decideix mirant l'estat de la resposta inicial (401), abans que ningú
 * n'hagi llegit res: un stream que ja ha començat no es repeteix mai. La segona resposta es
 * torna tal qual, també si és un altre 401: no hi ha bucle.
 */
async function authorizedFetch(
  url: string,
  init: FetchInit,
  allowRefresh: boolean,
): Promise<Response> {
  // Cap a un altre origen: ni token ni refresc.
  if (!isApiUrl(url)) return fetch(url, init);

  const sentToken = getAccessToken();
  const response = await fetch(url, withToken(init, sentToken));

  if (response.status !== 401 || !allowRefresh) return response;
  if (hasAuthorization(init.headers) || isSessionEndpoint(url)) return response;
  if (!isReplayableBody(init.body)) return response;
  // Sense refresh token no hi havia sessió (un hoste en una pàgina pública): el 401 es
  // torna a qui ha fet la crida, sense redirigir.
  if (!getRefreshToken()) return response;

  // Si una altra petició ja ha refrescat mentre aquesta viatjava, n'hi ha prou de repetir-la
  // amb el token nou: no cal gastar un altre refresc (el backend en limita el ritme).
  let token = getAccessToken();
  if (!token || token === sentToken) {
    const result = await refreshSession();
    if (result.outcome === 'rejected') return response;
    if (result.outcome === 'unavailable') throw new SessionRefreshUnavailableError();
    token = result.accessToken;
  }

  // El cos del 401 ja no el llegirà ningú: es deixa anar la connexió.
  void response.body?.cancel().catch(() => undefined);
  return fetch(url, withToken(init, token));
}

export interface AuthFetchInit extends FetchInit {
  /** Paràmetres de cerca, com a `api.get(url, { params })`. */
  params?: Record<string, any>;
}

/**
 * Crida autenticada que torna la `Response` crua: per a SSE/streaming (el xat de Miranda) i
 * per a pujades de fitxers, on `api.get`/`api.post` no serveixen perquè llegeixen el cos sencer.
 * Fa el mateix 401 → refresc → un reintent que la resta del client.
 *
 * - `path` és relatiu a l'API (`/agent/chat/router`) o una URL sencera.
 * - No llença per un estat HTTP: mira `response.ok` com amb `fetch`. Sí que llença si falla
 *   la xarxa o si el refresc no s'ha pogut fer (`SessionRefreshUnavailableError`).
 * - El cos s'ha de poder tornar a enviar: text, `FormData`, `Blob`/`File`, `URLSearchParams`
 *   o un buffer. Amb un `ReadableStream` no hi ha reintent i el 401 es torna tal qual.
 * - No posis `Content-Type` amb un `FormData`: el navegador hi ha de posar el `boundary`.
 */
export function authFetch(path: string, init: AuthFetchInit = {}): Promise<Response> {
  const { params, ...rest } = init;
  return authorizedFetch(buildUrl(path, params), rest, true);
}

async function coreRequest<T = any>(
  method: string,
  url: string,
  body?: any,
  config: RequestConfig = {},
): Promise<ApiResponse<T>> {
  const finalUrl = buildUrl(url, config.params);
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const isBlobLike = body instanceof Blob;

  const headers: Record<string, string> = {
    ...(config.headers || {}),
  };

  let payload: BodyInit | undefined;
  if (body === undefined || body === null) {
    payload = undefined;
  } else if (isFormData || isBlobLike || typeof body === 'string') {
    payload = body as BodyInit;
  } else {
    payload = JSON.stringify(body);
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  // 401 → refresc → un reintent: el fa `authorizedFetch`, el mateix circuit que `authFetch`.
  // El cos (`payload`) és text, `FormData` o `Blob`: es pot tornar a enviar.
  const response = await authorizedFetch(
    finalUrl,
    { method, headers, body: payload, signal: config.signal },
    !config._retry,
  );

  const mergedConfig = { ...config, url: finalUrl, method };

  // Parse body
  let data: any;
  const responseType = config.responseType || 'json';
  if (response.status === 204 || response.status === 205) {
    data = undefined;
  } else if (responseType === 'blob') {
    data = await response.blob();
  } else if (responseType === 'text') {
    data = await response.text();
  } else if (responseType === 'arraybuffer') {
    data = await response.arrayBuffer();
  } else {
    const text = await response.text();
    if (!text) {
      data = undefined;
    } else {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
  }

  const apiResponse: ApiResponse<T> = {
    data: data as T,
    status: response.status,
    statusText: response.statusText,
    headers: headersToObject(response.headers),
    config: mergedConfig,
  };

  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && (data.message || data.error)) ||
      response.statusText ||
      `HTTP ${response.status}`;
    const err = new ApiError<T>(
      typeof message === 'string' ? message : JSON.stringify(message),
      apiResponse,
    );
    err.config = mergedConfig;
    throw err;
  }

  return apiResponse;
}

const api = {
  get: <T = any>(url: string, config?: RequestConfig) =>
    coreRequest<T>('GET', url, undefined, config),
  // `data` = body del DELETE (mateix nom que a `request`); sense això el body es perdia en silenci.
  delete: <T = any>(url: string, config?: RequestConfig & { data?: any }) =>
    coreRequest<T>('DELETE', url, config?.data, config),
  post: <T = any>(url: string, body?: any, config?: RequestConfig) =>
    coreRequest<T>('POST', url, body, config),
  put: <T = any>(url: string, body?: any, config?: RequestConfig) =>
    coreRequest<T>('PUT', url, body, config),
  patch: <T = any>(url: string, body?: any, config?: RequestConfig) =>
    coreRequest<T>('PATCH', url, body, config),
  request: <T = any>(config: RequestConfig & { url: string; method?: string; data?: any }) =>
    coreRequest<T>(
      (config.method || 'GET').toUpperCase(),
      config.url,
      (config as any).data,
      config,
    ),
};

export interface Room {
  id: string;
  beds24RoomId: number;
  beds24PropertyId: number | null;
  name: string;
  type: string;
  description: string | null;
  capacity: number;
  floor: number;
  bedType: string;
  amenities: string;
  basePrice: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Unit {
  id: string;
  beds24UnitId: number;
  beds24RoomId: number;
  beds24PropertyId: number;
  name: string;
  displayName: string;
  physicalLocation?: string;
  shortName?: string;
  sortOrder?: number;
  isActive: boolean;
  /** 🛏️ Sprint 31 — Config de llits per defecte de la unit (JSON string). null = sense config */
  defaultBedConfig?: string | null;
  room: {
    id: string;
    name: string;
    type: string;
    displayName?: string;
    physicalLocation?: string;
    isVirtual?: boolean;
  } | null;
}

export interface Booking {
  id: string;
  beds24Id: string;
  guestName: string;
  guestEmail: string | null;
  guestPhone: string | null;
  /** E.164 per a Trucar/WhatsApp; null si és farciment o no té prefix internacional */
  guestPhoneE164?: string | null;
  /** Propietat de Beds24 de la reserva (el botó Codi només surt a la 18770) */
  beds24PropertyId?: number | null;
  guestCountry: string | null;
  guestCountry2: string | null;
  guestCompany: string | null;
  checkIn: string;
  checkOut: string;
  numNights: number;
  numAdults: number;
  numChildren: number;
  status: 'PENDING' | 'CONFIRMED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'CANCELLED' | 'NO_SHOW';
  channel: string;
  roomId: string;
  unitId: string | null; // 🆕 Añadido
  room: {
    id: string;
    name: string;
    type: string;
  };
  totalPrice: number | null;
  arrivalTime: string | null;
  comments: string | null;
  createdAt: string;
  updatedAt: string;
  // 🆕 Guest count for calendar badges
  totalGuests: number;
  guestsRegistered: number;
  // 🆕 CU-8: Client de facturació
  billingClientName?: string | null;
  // 🆕 Primary guest name (titular)
  primaryGuestName?: string | null;
  // 🆕 Info canal Beds24 (readonly)
  referer?: string | null;
  apiSource?: string | null;
  // 🆕 Idioma i WhatsApp
  lang?: string | null;
  hasWhatsapp?: boolean | null;
  /** Grup de Beds24: `beds24Id` de la reserva mestra (null = no és filla de cap grup) */
  masterId?: string | null;
  // 🆕 Sprint 29 — Payment tracking
  balance?: number | null;
  deposit?: number | null;
  paymentStatus?: 'PENDING' | 'PAID' | 'PARTIAL' | 'REFUNDED' | null;
  paymentMethod?: 'CASH' | 'CARD' | 'TRANSFER' | 'STRIPE' | 'BOOKING_OTA' | 'EXPEDIA_OTA' | null;
  paidAt?: string | null;
  // 🛏️ Sprint 31 — Bed config per reserva
  hasPet?: boolean;
  needsCrib?: boolean;
  /** JSON string: [{"type":"double","qty":1},{"type":"crib","qty":1}]. null = usar default de la unit */
  bedConfig?: string | null;
  // 🛏️ Runbook 10 (llits-1) — contracte C5
  /** Qui ha posat `bedConfig`: 'manual' (Desar/Confirmar), 'auto' (preferència del client), null = cap. */
  bedConfigSource?: 'manual' | 'auto' | null;
  /** Preferència de llit del client (sync de Beds24). */
  bedPreference?: 'twin' | 'double' | 'other' | null;
  /** Avís calculat pel backend en llegir (mai escrit a la DB). */
  bedWarning?: 'wants_twin_fixed_double' | 'wants_double_no_double' | 'mixed_preference' | null;
  /**
   * «Llits per revisar» (runbook 20): el backend decideix la regla sencera — estada oberta, reserva
   * viva i llits automàtics o un avís sense confirmar. Absent (backend vell) = no.
   */
  bedReviewPending?: boolean;
  /** Senyals de la card (`plans/calendar-card-signals.md` §2), calculats pel backend en llegir. */
  signals?: BookingSignals;
}

export const calendarApi = {
  getBookings: async (from: string, to: string, propertyId?: number): Promise<Booking[]> => {
    // Primary request with optional property filter
    const params: Record<string, string | number> = { from, to };
    if (propertyId) params.propertyId = propertyId;

    const response = await api.get(`/calendar/bookings`, { params });
    return response.data as Booking[];
  },

  getRooms: async (): Promise<Room[]> => {
    const response = await api.get(`/rooms`);
    return response.data;
  },

  // 🆕 Obtener units (habitaciones físicas)
  getUnits: async (propertyId?: number): Promise<Unit[]> => {
    const params = propertyId ? { propertyId } : {};
    const response = await api.get(`/units`, { params });
    return response.data.units || response.data;
  },
};

/**
 * Estades del PMS antic per al calendari històric (`plans/legacy-calendar-readonly.md`).
 *
 * Només ADMIN i només lectura: no hi ha cap `post`/`put` aquí i no n'hi ha d'haver.
 * El backend rebutja finestres de més de 62 dies.
 */
export const legacyStaysApi = {
  getCalendar: async (from: string, to: string): Promise<LegacyStayCalendarItem[]> => {
    const response = await api.get<LegacyStaysCalendarResponse>('/legacy-stays', {
      params: { from, to },
    });
    return response.data.stays;
  },
};

// 🆕 API para gestión de Persons (deduplicación)
export const personsApi = {
  search: async (query: string) => {
    const response = await api.get('/persons/search', {
      params: { q: query },
    });
    return response.data;
  },

  getHistory: async (personId: string) => {
    const response = await api.get(`/persons/${personId}/history`);
    return response.data;
  },
};

// ============================================================================
// AGENT / CHAT API
// ============================================================================

export interface AgentConfig {
  id: string;
  name: string;
  description: string | null;
  provider: string;
  model: string;
  role: string;
  parentId: string | null;
  routingDescription: string | null;
  sortOrder: number;
  isActive: boolean;
  adminOnly: boolean;
  children?: AgentConfig[];
}

export interface ChatSession {
  id: string;
  agentId: string;
  agent?: { id: string; name: string };
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  toolCalls?: any[];
  tokensIn?: number;
  tokensOut?: number;
  createdAt: string;
}

/**
 * Una tool tal com la publica `GET /agent/tool-catalog` (R18).
 *
 * `category` és la del `TOOL_CATALOG` del backend, o `sense-catàleg` per a les que existeixen
 * al codi i ningú ha donat d'alta. El formulari d'agents no té cap llista pròpia de tools.
 */
export interface ToolCatalogItem {
  id: string;
  description: string;
  category: string;
  tier: 1 | 2;
}

export const agentApi = {
  // Get available agents for current user
  getAgents: async (): Promise<AgentConfig[]> => {
    const response = await api.get('/agent/agents');
    return response.data;
  },

  // Catàleg complet de tools per al formulari d'agents (ADMIN)
  getToolCatalog: async (): Promise<ToolCatalogItem[]> => {
    const response = await api.get('/agent/tool-catalog');
    return response.data;
  },

  // Get agent by ID
  getAgent: async (agentId: string): Promise<AgentConfig> => {
    const response = await api.get(`/agent/agents/${agentId}`);
    return response.data;
  },

  // Get all sessions for current user
  getSessions: async (): Promise<ChatSession[]> => {
    const response = await api.get('/agent/sessions');
    return response.data;
  },

  // Get session by ID with messages
  getSession: async (sessionId: string): Promise<ChatSession> => {
    const response = await api.get(`/agent/sessions/${sessionId}`);
    return response.data;
  },

  // Delete a session
  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/agent/sessions/${sessionId}`);
  },

  // Get sessions for a specific agent
  getSessionsByAgent: async (agentId: string): Promise<ChatSession[]> => {
    const response = await api.get(`/agent/sessions/agent/${agentId}`);
    return response.data;
  },

  // Get chat stats
  getStats: async (): Promise<{
    totalSessions: number;
    totalMessages: number;
    totalTokensIn: number;
    totalTokensOut: number;
  }> => {
    const response = await api.get('/agent/stats');
    return response.data;
  },

  // Get API base URL for streaming
  getApiBaseUrl: () => API_BASE_URL,
};

export { api };
export default api;
